import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import {
  Loader2, RefreshCw, Plus, CheckSquare, Square, BookmarkCheck, X,
  Sparkles, Filter, Download,
} from "lucide-react";
import { toast } from "sonner";
import { formatCpc, currencySymbolForLocation } from "../../lib/currency";
import { rowsToCsv, downloadCsv, slugForFilename, todayStamp } from "../../lib/csv";

function fmt(n) {
  if (n == null) return "—";
  if (typeof n === "number") {
    if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
    if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
    return String(n);
  }
  return String(n);
}

const DEFAULT_FILTERS = { topPos: false, hiVol: false, lowKd: false };

export default function KeywordsTab({ client, reloadClient }) {
  const [data, setData] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [removingKw, setRemovingKw] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/seo/ranked-keywords?limit=100&client_id=${client.id}`, {
        domain: client.domain,
        location_name: client.location_name,
        language_name: client.language_name,
      });
      setData(data.result);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const { data } = await api.post(`/seo/ranked-keywords?limit=100&client_id=${client.id}`, {
          domain: client.domain,
          location_name: client.location_name,
          language_name: client.language_name,
        });
        if (alive) {
          setData(data.result);
          setSavedAt(data.saved_at || null);
          setLoadError(null);
          setSelected(new Set());
        }
      } catch (e) {
        if (alive) {
          const msg = e?.response?.data?.detail || "Keyword research failed";
          setLoadError(msg);
          toast.error(msg);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [client.domain, client.location_name, client.language_name]);

  const allItems = data?.items || [];

  const trackedSet = useMemo(
    () => new Set((client.keywords || []).map((k) => k.toLowerCase())),
    [client.keywords]
  );

  // Filtering layer
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter((it) => {
      const kw = it.keyword_data?.keyword || "";
      if (q && !kw.toLowerCase().includes(q)) return false;
      const info = it.keyword_data?.keyword_info || {};
      const pos = it.ranked_serp_element?.serp_item?.rank_group;
      const vol = info.search_volume;
      const kd = it.keyword_data?.keyword_properties?.keyword_difficulty;
      if (filters.topPos && (pos == null || pos > 10)) return false;
      if (filters.hiVol && (vol == null || vol < 1000)) return false;
      if (filters.lowKd && (kd == null || kd > 30)) return false;
      return true;
    });
  }, [allItems, filters, search]);

  const selectableItems = items.filter((it) => {
    const kw = it.keyword_data?.keyword;
    return kw && !trackedSet.has(kw.toLowerCase());
  });

  const toggleOne = (kw) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kw)) next.delete(kw);
      else next.add(kw);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === selectableItems.length && selectableItems.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableItems.map((it) => it.keyword_data.keyword)));
    }
  };

  const addToTracked = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      const seen = new Set();
      const merged = [];
      for (const k of client.keywords || []) {
        const lo = k.toLowerCase();
        if (!seen.has(lo)) { seen.add(lo); merged.push(k); }
      }
      for (const k of selected) {
        const lo = k.toLowerCase();
        if (!seen.has(lo)) { seen.add(lo); merged.push(k); }
      }
      await api.patch(`/clients/${client.id}`, { keywords: merged });
      toast.success(`Added ${selected.size} keyword${selected.size > 1 ? "s" : ""} to tracked.`);
      setSelected(new Set());
      reloadClient?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add keywords");
    } finally {
      setAdding(false);
    }
  };

  const removeTracked = async (kw, e) => {
    e?.stopPropagation();
    if (!kw) return;
    setRemovingKw(kw);
    try {
      const next = (client.keywords || []).filter((k) => k.toLowerCase() !== kw.toLowerCase());
      await api.patch(`/clients/${client.id}`, { keywords: next });
      toast.success(`Removed "${kw}" from tracked.`);
      reloadClient?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    } finally {
      setRemovingKw(null);
    }
  };

  const applyQuickWins = () => {
    setFilters({ topPos: false, hiVol: true, lowKd: true });
    setSearch("");
    // Also pre-select quick wins (position 4-10, vol >= 100, KD <= 40)
    const qw = allItems.filter((it) => {
      const kw = it.keyword_data?.keyword;
      if (!kw || trackedSet.has(kw.toLowerCase())) return false;
      const info = it.keyword_data?.keyword_info || {};
      const pos = it.ranked_serp_element?.serp_item?.rank_group;
      const vol = info.search_volume;
      const kd = it.keyword_data?.keyword_properties?.keyword_difficulty;
      return pos != null && pos >= 4 && pos <= 10 && (vol ?? 0) >= 100 && (kd ?? 100) <= 40;
    });
    if (qw.length === 0) {
      toast.info("No 'quick win' keywords found for this domain.");
      return;
    }
    setSelected(new Set(qw.map((it) => it.keyword_data.keyword)));
    toast.success(`Selected ${qw.length} quick-win keyword${qw.length > 1 ? "s" : ""}.`);
  };

  const allChecked = selected.size > 0 && selected.size === selectableItems.length;
  const someChecked = selected.size > 0 && !allChecked;
  const activeFilterCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  const exportCsv = () => {
    if (!items.length) return;
    const currency = currencySymbolForLocation(client.location_name).trim() || "$";
    const columns = [
      { header: "Keyword", accessor: (it) => it.keyword_data?.keyword ?? "" },
      { header: "Tracked", accessor: (it) => trackedSet.has((it.keyword_data?.keyword || "").toLowerCase()) ? "Yes" : "No" },
      { header: "Position", accessor: (it) => it.ranked_serp_element?.serp_item?.rank_group ?? "" },
      { header: "Search Volume", accessor: (it) => it.keyword_data?.keyword_info?.search_volume ?? "" },
      { header: `CPC (${currency})`, accessor: (it) => it.keyword_data?.keyword_info?.cpc ?? "" },
      { header: "KD", accessor: (it) => it.keyword_data?.keyword_properties?.keyword_difficulty ?? "" },
      { header: "URL", accessor: (it) => it.ranked_serp_element?.serp_item?.url ?? "" },
      { header: "Location", accessor: () => client.location_name || "" },
    ];
    const csv = rowsToCsv(items, columns);
    const filename = `${slugForFilename(client.domain)}-keywords-${todayStamp()}.csv`;
    downloadCsv(filename, csv);
    toast.success(`Exported ${items.length} keywords`);
  };
  const toggleFilter = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));

  return (
    <div className="space-y-4" data-testid="keywords-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">
            Ranking keywords
          </div>
          <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">{client.domain}</h3>
          <p className="text-xs text-[#718096] mt-1" data-testid="keywords-freshness">
            {savedAt
              ? `Frozen · showing saved copy from ${new Date(savedAt).toLocaleDateString()} — no live lookups.`
              : "Live from DataForSEO Labs · Tracked keywords feed into rank tracking + monthly digest."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              onClick={addToTracked}
              disabled={adding}
              data-testid="keywords-add-to-tracked"
              className="btn-primary inline-flex items-center gap-2"
            >
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add {selected.size} to tracked
            </button>
          )}
          <button
            onClick={load}
            disabled={loading || client.frozen}
            title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
            data-testid="keywords-refresh"
            className="btn-ghost inline-flex items-center gap-2"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh
          </button>
          {items.length > 0 && (
            <button
              onClick={exportCsv}
              data-testid="keywords-export-csv"
              title="Download keywords as CSV"
              className="btn-ghost inline-flex items-center gap-2"
            >
              <Download className="h-3 w-3" />
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Filter toolbar */}
      <div className="card-soft p-3 flex items-center gap-2 flex-wrap" data-testid="keywords-filters">
        <div className="flex items-center gap-1.5 text-xs text-[#718096] font-semibold pl-1 pr-1">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>
        <FilterPill k="topPos" label="Position ≤ 10" testid="filter-top-position" filters={filters} onToggle={toggleFilter} />
        <FilterPill k="hiVol" label="Volume ≥ 1k" testid="filter-high-volume" filters={filters} onToggle={toggleFilter} />
        <FilterPill k="lowKd" label="KD ≤ 30" testid="filter-low-kd" filters={filters} onToggle={toggleFilter} />
        <div className="h-5 w-px bg-[#e2e8f0] mx-1" />
        <button
          onClick={applyQuickWins}
          data-testid="filter-quick-wins"
          className="text-xs font-semibold rounded-full px-3 py-1.5 bg-[#4fd1c5]/10 text-[#2c7a7b] hover:bg-[#4fd1c5]/20 inline-flex items-center gap-1.5 transition-colors"
          title="Position 4-10, volume ≥ 100, KD ≤ 40"
        >
          <Sparkles className="h-3.5 w-3.5" /> Quick wins
        </button>
        <div className="flex-1 min-w-[180px]">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="keywords-search"
            placeholder="Search keywords…"
            className="w-full bg-white border border-[#e2e8f0] rounded-full px-3.5 py-1.5 text-xs focus:outline-none focus:border-[#4fd1c5] focus:ring-2 focus:ring-[#4fd1c5]/15 transition-all"
          />
        </div>
        {activeFilterCount > 0 && (
          <button
            onClick={() => { setFilters(DEFAULT_FILTERS); setSearch(""); }}
            data-testid="filter-clear"
            className="text-[11px] font-semibold text-[#a0aec0] hover:text-[#4a5568] inline-flex items-center gap-1"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
        <div className="text-[11px] text-[#a0aec0] font-mono-tight ml-auto">
          {items.length} / {allItems.length} rows
        </div>
      </div>

      <div className="card-soft overflow-hidden">
        <div className="grid grid-cols-12 gap-x-4 px-5 py-3.5 text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold border-b border-[#edf2f7] bg-[#f7fafc]">
          <div className="col-span-1">
            <button
              onClick={toggleAll}
              disabled={selectableItems.length === 0}
              data-testid="keywords-toggle-all"
              className="text-[#4a5568] hover:text-[#2c7a7b] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title={allChecked ? "Clear selection" : "Select all untracked"}
            >
              {allChecked ? (
                <CheckSquare className="h-4 w-4 text-[#38b2ac]" />
              ) : (
                <Square className={`h-4 w-4 ${someChecked ? "text-[#38b2ac]" : ""}`} />
              )}
            </button>
          </div>
          <div className="col-span-4">Keyword</div>
          <div className="col-span-1 text-right">Pos</div>
          <div className="col-span-2 text-right">Volume</div>
          <div className="col-span-1 text-right">CPC ({currencySymbolForLocation(client.location_name).trim() || "$"})</div>
          <div className="col-span-1 text-right">KD</div>
          <div className="col-span-2 truncate">URL</div>
        </div>
        {loading && (
          <div className="p-8 text-center text-sm text-[#a0aec0] font-mono-tight">
            Fetching keywords from DataForSEO…
          </div>
        )}
        {!loading && allItems.length === 0 && (
          <div className="p-8 text-center text-sm text-[#a0aec0]" data-testid="keywords-empty">
            {loadError || "No ranking keywords found for this domain."}
          </div>
        )}
        {!loading && allItems.length > 0 && items.length === 0 && (
          <div className="p-8 text-center text-sm text-[#a0aec0]">
            No keywords match the current filters.
          </div>
        )}
        <div className="divide-y divide-[#edf2f7] max-h-[640px] overflow-y-auto">
          {items.map((it, i) => {
            const kw = it.keyword_data?.keyword || "—";
            const info = it.keyword_data?.keyword_info || {};
            const ranked = it.ranked_serp_element?.serp_item || {};
            const pos = ranked.rank_group;
            const url = ranked.url || "";
            const kd = it.keyword_data?.keyword_properties?.keyword_difficulty;
            const isTracked = trackedSet.has(kw.toLowerCase());
            const isSelected = selected.has(kw);
            const isRemoving = removingKw === kw;
            return (
              <div
                key={i}
                data-testid={`kw-row-${i}`}
                onClick={() => !isTracked && toggleOne(kw)}
                className={`grid grid-cols-12 gap-x-4 items-center px-5 py-2.5 text-sm transition-colors ${
                  isTracked
                    ? "bg-[#4fd1c5]/[0.06]"
                    : isSelected
                    ? "bg-[#4fd1c5]/[0.12] cursor-pointer"
                    : "hover:bg-[#f7fafc] cursor-pointer"
                }`}
              >
                <div className="col-span-1">
                  {isTracked ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-bold text-[#2c7a7b] bg-[#4fd1c5]/15 px-1.5 py-0.5 rounded"
                      title="Already tracked"
                      data-testid={`kw-tracked-badge-${i}`}
                    >
                      <BookmarkCheck className="h-3 w-3" />
                    </span>
                  ) : isSelected ? (
                    <CheckSquare className="h-4 w-4 text-[#38b2ac]" data-testid={`kw-checkbox-${i}`} />
                  ) : (
                    <Square className="h-4 w-4 text-[#cbd5e0]" data-testid={`kw-checkbox-${i}`} />
                  )}
                </div>
                <div className="col-span-4 truncate flex items-center gap-2 text-[#1a202c]">
                  <span className="truncate">{kw}</span>
                  {isTracked && (
                    <button
                      onClick={(e) => removeTracked(kw, e)}
                      disabled={isRemoving}
                      data-testid={`kw-remove-tracked-${i}`}
                      title="Remove from tracked"
                      className="shrink-0 h-5 w-5 rounded-full inline-flex items-center justify-center text-[#a0aec0] hover:text-[#c53030] hover:bg-[#fed7d7]/50 disabled:opacity-50 transition-colors"
                    >
                      {isRemoving ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <X className="h-3 w-3" strokeWidth={2.4} />
                      )}
                    </button>
                  )}
                </div>
                <div className="col-span-1 text-right font-mono-tight font-semibold">
                  {pos != null ? (
                    <span className={pos <= 10 ? "text-[#38b2ac]" : "text-[#4a5568]"}>{pos}</span>
                  ) : "—"}
                </div>
                <div className="col-span-2 text-right font-mono-tight text-[#4a5568]">{fmt(info.search_volume)}</div>
                <div className="col-span-1 text-right font-mono-tight text-[#4a5568]">
                  {formatCpc(info.cpc, client.location_name)}
                </div>
                <div className="col-span-1 text-right font-mono-tight text-[#4a5568]">{kd ?? "—"}</div>
                <div className="col-span-2 truncate text-xs text-[#718096] font-mono-tight">
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="hover:text-[#2c7a7b]"
                  >
                    {url.replace(/^https?:\/\//, "").slice(0, 28)}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FilterPill({ k, label, testid, filters, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(k)}
      data-testid={testid}
      className={`text-xs font-semibold rounded-full px-3 py-1.5 transition-colors border ${
        filters[k]
          ? "bg-brand text-white border-transparent shadow-[0_4px_10px_-2px_rgba(56,178,172,0.4)]"
          : "bg-white text-[#4a5568] border-[#e2e8f0] hover:border-[#4fd1c5] hover:text-[#2c7a7b]"
      }`}
    >
      {label}
    </button>
  );
}

